/* eslint-disable camelcase */
import {
  createSelector,
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import { DefaultRootState } from 'react-redux';
import findAvailableLocale from './helpers/findAvailableLocale';
import { selectLocale } from './localization';
import { EventConfig } from './types/eventConfig';

const selectBannerAssets = (state: any) => (state?.eventConfigs?.lobby?.bannerAssets || []) as any[];

export const selectDesktopBanners = createSelector(
  selectBannerAssets,
  (bannerAssets) => bannerAssets
    .slice()
    .sort((current, next) => ((current?.order || 0) > (next?.order || 0) ? 1 : -1))
    .map((it) => ({
      ...it,
      bannerAsset: (it.bannerAsset || [])
        .slice()
        .sort((current, next) => (current?.desktopOrder > next?.desktopOrder ? 1 : -1)),
    })),
);

export const selectMobileBanners = createSelector(
  selectBannerAssets,
  (bannerAssets) => bannerAssets
    .flatMap((list) => (list?.bannerAsset || [])
      .filter((item) => ['IMAGE', 'VIDEO'].includes(item.type) && item.mobileOrder > -1))
    .sort((current, next) => (current.mobileOrder > next.mobileOrder ? 1 : -1)),
);

export const selectLocalizedEventName = createSelector(
  [(state: any) => state.eventConfigs?.name || '', selectLocale],
  (name, locale) => {
    if (typeof name === 'string') return name;
    return findAvailableLocale(name || {}, locale) || '';
  },
);

export const selectWebinarInterval = (state) => (state.eventConfigs?.webinar?.webinarInterval);

export const selectFavicon = ((state) => state.eventConfigs.favicon);

export const selectEventConfig = (state) => state.eventConfigs as EventConfig;

export const EventConfigsSlice = createSlice<EventConfig, {
  setEventConfigs:(_: EventConfig, __: PayloadAction<EventConfig>) => any,
    }>({
      name: 'event-configs',
      initialState: {} as EventConfig,
      reducers: {
        setEventConfigs(state: EventConfig, action: PayloadAction<EventConfig>) {
          let featuresList = [];
          if (action.payload.featuresToggle) {
            Object.keys(action.payload.featuresToggle).forEach((key) => action.payload.featuresToggle[key] && featuresList.push(key));
          } else {
            featuresList = action.payload.features;
          }
          return {
            pageNames: {},
            products: {},
            ...action.payload,
            features: featuresList,
          };
        },
      },
    });

export const {
  setEventConfigs,
} = EventConfigsSlice.actions;

export const updateEventConfigs = createAsyncThunk(
  'event-configs/updateEventConfigs',
  async (newState: EventConfig, { getState, dispatch }) => {
    const state = getState() as DefaultRootState;
    const currentConfig = state.eventConfigs;
    const newConfig = { ...currentConfig, ...newState };
    if (!isEqual(currentConfig, newConfig)) {
      dispatch(setEventConfigs(newConfig as EventConfig));
      console.log('Updated event configuration data.');
    }
  },
);

export default EventConfigsSlice.reducer;
